/**
 * Finance › Dinheiro em mãos (W7-CASH-HANDOVER-CONSOLE, D411; the D392 A2 routes) — framework-neutral view-models for the
 * screen where the courier's cash debt is settled under four eyes: one operator records the cash a courier handed over,
 * a DIFFERENT one confirms it (which posts) or rejects it (which moves no money).
 *
 * The console only FILTERS and EXPLAINS. It hides Confirm from whoever recorded the handover and from the courier
 * himself (with the reason shown), and it shows what the LEDGER says the courier owes (`GET /v1/payouts/context`,
 * `owed.receivableMinor`) less the handovers already awaiting a confirmation — the netting the server applies at the
 * record (W6Y-02). The authority is the server: it compares the SESSION identities (`403 SELF_APPROVAL` /
 * `SELF_HANDOVER`), caps the record at that netted debt and re-reads the debt at the confirmation (`409
 * HANDOVER_EXCEEDS_DEBT`), whatever the console shows. Money stays integer minor units end to end, in the currency the
 * courier's market governs — never typed.
 */
import {
  type ApiError,
  type CashHandoverApi,
  type CashHandoverDecided,
  type CashHandoverRecorded,
  type CashHandoverStatus,
  type CashHandoverView,
  type CouriersHoldingCash,
  type LedgerOwed,
} from '@vello/api-client';
import { type Result } from '@vello/core';
import { grantsAllow, type KnownGrants } from './console-access.js';

/** The queue's statuses, in lifecycle order (the server's `CashHandoverStatus`). */
export const CASH_HANDOVER_QUEUE_STATUSES = ['RECORDED', 'CONFIRMED', 'REJECTED'] as const satisfies readonly CashHandoverStatus[];
export type CashHandoverQueueStatus = (typeof CASH_HANDOVER_QUEUE_STATUSES)[number];

/** The most rows one queue read returns (the server's cap). A read that returns this many may have been cut. */
export const CASH_HANDOVER_QUEUE_LIMIT = 200;
/** The server's limits on the maker's reference and the checker's reason. */
export const CASH_HANDOVER_REFERENCE_MAX = 200;
export const CASH_HANDOVER_REASON_MAX = 500;

export type CashHandoverTone = 'neutral' | 'success' | 'warning' | 'danger';

export function cashHandoverStatusTone(status: string): CashHandoverTone {
  return status === 'RECORDED' ? 'warning' : status === 'CONFIRMED' ? 'success' : status === 'REJECTED' ? 'danger' : 'neutral';
}

/** Ids are uuids the server compares case-insensitively. */
const sameId = (a: string, b: string): boolean => a.trim().toLowerCase() === b.trim().toLowerCase();

/** Why Confirm is NOT offered to this operator on this handover, or undefined when it is. Display only. */
export type CashHandoverConfirmBlock = 'NOT_PENDING' | 'OWN_RECORD' | 'OWN_CASH' | 'NO_PERMISSION';

/** D868: `selfApproval` lifts OWN_RECORD (the maker rule) for a super administrator; OWN_CASH (one's own cash as the courier) stays. */
export function cashHandoverConfirmBlock(row: Pick<CashHandoverView, 'status' | 'recordedBy' | 'courierId'>, operatorId: string | undefined, mayDecide: boolean, selfApproval = false): CashHandoverConfirmBlock | undefined {
  if (row.status !== 'RECORDED') return 'NOT_PENDING';
  if (!selfApproval && operatorId !== undefined && sameId(row.recordedBy, operatorId)) return 'OWN_RECORD';
  if (operatorId !== undefined && sameId(row.courierId, operatorId)) return 'OWN_CASH';
  if (!mayDecide) return 'NO_PERMISSION';
  return undefined;
}

/**
 * Whether this operator may reject a handover: any RECORDED one, by whoever holds the decision word (`payout:settle`)
 * — its own maker included: a rejection moves no money, so it is a withdrawal of a mistaken record.
 */
export function cashHandoverRejectable(row: Pick<CashHandoverView, 'status'>, mayDecide: boolean): boolean {
  return mayDecide && row.status === 'RECORDED';
}

/**
 * The operator's authority on the screen, from its grants (unknown grants → the server decides). The three words are
 * PLATFORM-target on the server, like every `payout:*` route.
 */
export function cashHandoverAuthority(grants: KnownGrants): { readonly mayView: boolean; readonly mayRecord: boolean; readonly mayDecide: boolean } {
  if (grants === undefined) return { mayView: true, mayRecord: true, mayDecide: true };
  return {
    mayView: grantsAllow(grants, { permission: 'payout:view', target: 'PLATFORM' }),
    mayRecord: grantsAllow(grants, { permission: 'payout:create', target: 'PLATFORM' }),
    mayDecide: grantsAllow(grants, { permission: 'payout:settle', target: 'PLATFORM' }),
  };
}

/**
 * A courier's cash position as the desk shows it: the debt the LEDGER carries (his receivable, clamped at zero), what
 * RECORDED handovers already promise of it, and what is left to record. `awaitingComplete` is false when the queue that
 * was netted may have been cut at its limit — the screen then says "at least".
 */
export interface CashDebtPosition {
  readonly currency: string;
  readonly debtMinor: bigint;
  readonly awaitingMinor: bigint;
  readonly awaitingCount: number;
  readonly remainingMinor: bigint;
  readonly awaitingComplete: boolean;
}

const MINOR = /^-?\d+$/;

/**
 * The position of `courierId` from the ledger view and the market's RECORDED queue, or undefined when the server
 * reported no ledger (a pre-A2 server, or no governed currency). Undefined means "the desk does not know" — never zero.
 */
export function cashDebtPosition(owed: LedgerOwed | null | undefined, recorded: readonly CashHandoverView[], courierId: string, awaitingComplete: boolean): CashDebtPosition | undefined {
  if (owed === null || owed === undefined || !MINOR.test(owed.receivableMinor)) return undefined;
  const receivable = BigInt(owed.receivableMinor);
  const debtMinor = receivable > 0n ? receivable : 0n;
  let awaitingMinor = 0n;
  let awaitingCount = 0;
  for (const h of recorded) {
    if (h.status !== 'RECORDED' || h.currency !== owed.currency || !sameId(h.courierId, courierId) || !MINOR.test(h.amountMinor)) continue;
    awaitingMinor += BigInt(h.amountMinor);
    awaitingCount += 1;
  }
  const left = debtMinor - awaitingMinor;
  return { currency: owed.currency, debtMinor, awaitingMinor, awaitingCount, remainingMinor: left > 0n ? left : 0n, awaitingComplete };
}

/** Whether a typed amount is within what is left to record (display only — the server is the authority). */
export function cashHandoverWithinDebt(amountMinor: bigint | undefined, position: CashDebtPosition | undefined): boolean {
  return amountMinor === undefined || position === undefined || amountMinor <= position.remainingMinor;
}

/** Exact per-currency totals of a queue (bigint; never summed across currencies). */
export function summarizeCashHandovers(rows: readonly Pick<CashHandoverView, 'amountMinor' | 'currency'>[]): readonly { readonly currency: string; readonly totalMinor: string; readonly count: number }[] {
  const by = new Map<string, { total: bigint; count: number }>();
  for (const r of rows) {
    if (!/^\d+$/.test(r.amountMinor)) continue;
    const cur = by.get(r.currency) ?? { total: 0n, count: 0 };
    by.set(r.currency, { total: cur.total + BigInt(r.amountMinor), count: cur.count + 1 });
  }
  return [...by.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([currency, v]) => ({ currency, totalMinor: v.total.toString(), count: v.count }));
}

type HandoverRuntime = { readonly cashHandovers: CashHandoverApi };

/** The per-market queue, read at the server's cap (so a netting or a count knows when it may have been cut). */
export function loadCashHandoverQueue(runtime: HandoverRuntime, market: string, status: CashHandoverQueueStatus): Promise<Result<{ handovers: readonly CashHandoverView[] }, ApiError>> {
  return runtime.cashHandovers.queue({ market, status, limit: CASH_HANDOVER_QUEUE_LIMIT });
}

/**
 * Record a handover — the maker is the session; the amount is integer minor units of the courier's market currency; the
 * deposit reference is required (D450: the server refuses a record without one, `HANDOVER_REFERENCE_REQUIRED`).
 */
export function recordCashHandover(runtime: HandoverRuntime, input: { readonly courierId: string; readonly amountMinor: bigint; readonly reference: string }): Promise<Result<CashHandoverRecorded, ApiError>> {
  return runtime.cashHandovers.record({ courierId: input.courierId.trim(), amountMinor: input.amountMinor.toString(), reference: input.reference.trim() });
}

/**
 * D450 (W7-SWEEP, D414): the couriers of `market` holding the platform's cash now — the desk's starting list, largest
 * debt first. A failed read is an error the screen shows (never an empty list that reads as "nobody owes").
 */
export function loadCouriersHoldingCash(runtime: HandoverRuntime, market: string): Promise<Result<CouriersHoldingCash, ApiError>> {
  return runtime.cashHandovers.couriersHoldingCash(market);
}

/**
 * Who acted on a handover, as the screen names them (D450): "you" for the signed-in operator, the sign-in label the
 * queue carries, else a short id — never the whole uuid of someone the reader cannot otherwise identify.
 */
export function cashHandoverActor(
  id: string | null,
  label: string | null | undefined,
  operatorId: string | undefined,
): { readonly kind: 'you' } | { readonly kind: 'label' | 'id'; readonly text: string } | undefined {
  if (id === null || id === '') return undefined;
  if (operatorId !== undefined && sameId(id, operatorId)) return { kind: 'you' };
  if (label !== null && label !== undefined && label.trim() !== '') return { kind: 'label', text: label };
  return { kind: 'id', text: id.length > 8 ? id.slice(0, 8) + '…' : id };
}

/** Confirm or reject a RECORDED handover with a reason, in the market the queue was read in. */
export function decideCashHandover(runtime: HandoverRuntime, handoverId: string, input: { readonly decision: 'CONFIRM' | 'REJECT'; readonly reason: string; readonly market: string }): Promise<Result<CashHandoverDecided, ApiError>> {
  return runtime.cashHandovers.decide(handoverId, { decision: input.decision, reason: input.reason.trim(), market: input.market });
}
